/**
 * Mounts canvas input accounting for the whole app.
 *
 * Sits at the root rather than inside a canvas so it does not depend on which
 * drawing engine is live, and so a contact that arrives before or outside a
 * canvas is still accounted for.
 */

import { useEffect } from 'react';
import { isFeatureEnabled } from '@shared-ui/feature-gates';
import { getWebTracingManager, LogLevel, WebComponentCode } from '@shared-ui/logging';
import { installCanvasInputTelemetry } from './canvasInputTelemetry';

export function CanvasTelemetryMount({ userId }: { userId?: string }) {
    useEffect(() => installCanvasInputTelemetry(), []);

    useEffect(() => {
        // Volume only. Lost strokes report at ERROR either way — this decides
        // whether the per-contact detail around them travels too. Gates resolve
        // per user, so one student can be turned up on their own.
        //
        // Keyed on the user because the cached gate map is refreshed from the
        // profile endpoint after mount: reading it once at startup would miss a
        // gate enabled since the last visit until a second reload, which is a
        // trap for whoever is trying to collect the very logs this turns on.
        if (!isFeatureEnabled('canvas_input_tracing')) return;
        getWebTracingManager().setLocalComponentFloor(WebComponentCode.WEB_CANVAS, LogLevel.INFO3);
    }, [userId]);

    return null;
}
